import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  Group,
  NativeSelect,
  NumberInput,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useModals } from "@mantine/modals";
import { KnowledgeDeleteControls } from "@/components/knowledge/KnowledgeDeleteControls";
import { showNotification } from "@mantine/notifications";
import {
  IconBook,
  IconCheck,
  IconEye,
  IconFile,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconUsers,
  IconX,
} from "@/components/icons";
import { KnowledgeBaseExcelPanel } from "@/components/knowledge/KnowledgeBaseExcelPanel";
import { KnowledgeProjectTabs } from "@/components/knowledge/KnowledgeProjectTabs";
import {
  flushPendingKnowledgeAttachments,
  KnowledgeArticleAttachments,
} from "@/components/knowledge/KnowledgeArticleAttachments";
import { KnowledgeRichTextEditor } from "@/components/knowledge/KnowledgeRichTextEditor";
import {
  DetailPanel,
  KnowledgeBaseWorkspace,
  KNOWLEDGE_TAG_RULES_SECTION,
  KNOWLEDGE_TAGS_SECTION,
  KNOWLEDGE_VARIABLES_SECTION,
} from "@/components/knowledge/KnowledgeBaseWorkspace";
import {
  FALLBACK_MATERIAL_TYPES,
  KNOWLEDGE_GENERAL_TAB_ID,
  articleCountLabel,
  groupArticlesByProject,
  generalArticlesFromList,
  isKnowledgeGeneralTab,
} from "@/components/knowledge/knowledge-base-shared";
import "@/components/knowledge/knowledge-content.css";
import {
  AppModal,
  PageCenterLoader,
  PageShell,
  PaginationBar,
} from "@/components/ui";
import { useAuth } from "@/lib/auth-context";
import { api, ApiError } from "@/lib/api";
import { formatDateTimeDot } from "@/lib/ticket-display";
import { downloadBlob } from "@/lib/download-blob";
import {
  canAddKnowledgeBase,
  canDeleteKnowledgeBase,
  canEditKnowledgeBase,
  canAccessKnowledgeBase,
  loadMyPermissions,
  mergeWithDefaultPermissions,
  readCachedPermissions,
} from "@/lib/permissions";
import type {
  MyPermissionsResponse,
  ProjectRow,
  SupportKnowledgeArticleRow,
  SupportKnowledgeAttachmentRow,
  SupportKnowledgeMaterialType,
  SupportKnowledgeReadStatsResponse,
  SupportKnowledgeSearchRow,
  SupportKnowledgeSimilarItem,
  SupportKnowledgeTagRow,
  SupportKnowledgeTagRuleRow,
  SupportKnowledgeTagRuleWritePayload,
  SupportKnowledgeTagWritePayload,
  SupportKnowledgeTrainingAttemptRow,
  SupportKnowledgeTrainingQuestionRow,
  SupportKnowledgeTrainingQuestionType,
  SupportKnowledgeTrainingStatsResponse,
  SupportKnowledgeVariableDefinitionRow,
  SupportKnowledgeVariableWritePayload,
} from "@/types/api";
import {
  KnowledgeArticleContent,
  stripKnowledgeContent,
} from "@/components/knowledge/KnowledgeArticleContent";

const KNOWLEDGE_BASE_GUIDE_HTML = `
  <h2>Зачем нужна база знаний</h2>
  <p>База знаний в обращении должна помогать оператору быстро найти корректный материал и вставить клиенту только готовый ответ. В статье не нужно хранить лишние служебные комментарии, если они не помогают оператору принять решение.</p>

  <h2>Как выбрать тип материала</h2>
  <ul>
    <li><strong>FAQ</strong> — короткий вопрос и готовый ответ клиенту.</li>
    <li><strong>Макрос</strong> — текст, который оператор часто отправляет клиенту без ручной сборки.</li>
    <li><strong>Инструкция</strong> — пошаговые действия для оператора, когда нужно проверить настройки, платеж, устройство или другой сценарий.</li>
    <li><strong>Регламент</strong> — внутренние правила, ограничения, сроки, зоны ответственности.</li>
    <li><strong>Другое</strong> — использовать только если материал не подходит под типы выше.</li>
  </ul>

  <h2>Обязательная структура статьи</h2>
  <p>Заголовки можно писать через кнопки редактора или как смысловые блоки. Оператор в обращении видит эти блоки как нормальные разделы, а не как сырой текст.</p>
  <ul>
    <li><strong>Название</strong> — уникальное и понятное: проблема + действие или результат.</li>
    <li><strong>Теги</strong> — через запятую, без дублей: <em>оплата, устройства, wink, возврат</em>.</li>
    <li><strong>Содержимое</strong> — только актуальная информация, без повторов из других статей.</li>
    <li><strong>Связанные статьи</strong> — материалы, которые оператор может открыть следующим кликом.</li>
    <li><strong>Следующий шаг</strong> — одна статья, куда оператор должен перейти после текущего сценария.</li>
  </ul>

  <h2>Какие блоки вставляются в обращение</h2>
  <p>Кнопка вставки в обращении забирает не всю статью, а только готовый клиентский текст из специальных блоков.</p>
  <ul>
    <li><strong>Для макроса</strong> добавляйте раздел <strong>Готовый текст</strong>.</li>
    <li><strong>Для шаблона ответа</strong> добавляйте раздел <strong>Готовый ответ</strong>.</li>
    <li><strong>Для FAQ</strong> добавляйте раздел <strong>Ответ</strong>.</li>
  </ul>
  <blockquote>Если в статье нет одного из этих блоков, материал можно читать, но он не будет вставляться клиенту как готовый ответ.</blockquote>

  <h2>Пример макроса</h2>
  <h3>Плохо</h3>
  <p>В статье есть примечания оператору, история происхождения макроса и несколько похожих вариантов ответа. Оператору приходится вручную выбирать текст.</p>
  <h3>Хорошо</h3>
  <pre><code>## Когда использовать
Клиент спрашивает, почему нельзя подключить больше 5 устройств.

## Готовый текст
На одном аккаунте можно использовать до 5 устройств. Если лимит уже достигнут, удалите неактуальное устройство и повторите подключение.

## Связанные статьи
- FAQ: Сколько устройств можно подключить?
- Инструкция: Устройства и удаление устройств

## Следующий шаг
Далее: Макрос: проверить оба режима</code></pre>

  <h2>Связанные статьи</h2>
  <p>Используйте блок <strong>Связанные статьи</strong>, когда оператору могут понадобиться дополнительные материалы по этой же теме.</p>
  <pre><code>## Связанные статьи
- FAQ: Сколько устройств можно подключить?
- Макрос: лимит 5 устройств
- Инструкция: Устройства и удаление устройств</code></pre>
  <p>В редакторе удобнее использовать поле <strong>Статья для перехода</strong>: начните вводить название, выберите существующую статью и нажмите <strong>В связанные</strong>.</p>

  <h2>Следующий шаг</h2>
  <p>Используйте блок <strong>Следующий шаг</strong>, если после текущего материала оператор должен перейти к конкретной следующей статье.</p>
  <pre><code>## Следующий шаг
Далее: Макрос: проверить оба режима</code></pre>
  <p>Название после <strong>Далее:</strong> должно полностью совпадать с названием существующей статьи, тогда переход будет работать корректно.</p>

  <h2>Файлы и изображения</h2>
  <ul>
    <li>Скриншоты внутри инструкции вставляйте прямо в редактор, чтобы оператор видел их в тексте.</li>
    <li>Документы, PDF, таблицы и другие файлы прикрепляйте во вложениях статьи.</li>
    <li>Не вставляйте временные ссылки из личных чатов или внешних хранилищ.</li>
  </ul>

  <h2>Правила качества</h2>
  <ul>
    <li>Одна статья — один сценарий или один ответ. Не смешивайте разные проблемы.</li>
    <li>Не дублируйте одинаковые макросы. Лучше добавить связанные статьи.</li>
    <li>Не добавляйте в готовый ответ внутренние пометки: даты миграции, автора, «канонический макрос», комментарии для редактора.</li>
    <li>Проверяйте, что готовый ответ можно отправить клиенту без редактирования, кроме имени, даты, email или ссылки.</li>
    <li>Если материал устарел, исправьте статью или выключите активность, чтобы оператор не использовал неверный ответ.</li>
  </ul>
`;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const KNOWLEDGE_VIEW_ACTION_LABELS: Record<string, string> = {
  shown: "Показано",
  closed: "Закрыл",
  acknowledged: "Ознакомился",
};

const KNOWLEDGE_TABLE_PAGE_SIZE = 10;

function knowledgeTablePageCount(total: number): number {
  return Math.max(1, Math.ceil(total / KNOWLEDGE_TABLE_PAGE_SIZE));
}

function knowledgeTablePageRows<T>(rows: T[], page: number): T[] {
  const start = (Math.max(1, page) - 1) * KNOWLEDGE_TABLE_PAGE_SIZE;
  return rows.slice(start, start + KNOWLEDGE_TABLE_PAGE_SIZE);
}

function formatDuration(seconds?: number | null): string {
  if (seconds == null) return "—";
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  if (minutes <= 0) return `${rest} сек`;
  return `${minutes} мин ${rest} сек`;
}

function safeMaterialFilename(article: SupportKnowledgeArticleRow): string {
  const safeTitle =
    (article.title || "material")
      .trim()
      .replace(/[^a-zA-Zа-яА-Я0-9._-]+/g, "_")
      .replace(/_+/g, "_")
      .slice(0, 45) || "material";
  return `knowledge_material_${safeTitle}_${article.id.slice(0, 8)}.json`;
}

export function KnowledgeBasePage() {
  const { openConfirmModal } = useModals();
  const { user } = useAuth();
  const isWide = useMediaQuery(`(min-width: 1100px)`) ?? false;

  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() =>
    readCachedPermissions(),
  );
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm);
  }, [user?.role]);
  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role);
  const accountRole = user?.role;
  const canView = canAccessKnowledgeBase(effectivePerm, accountRole);
  const canAdd = canAddKnowledgeBase(effectivePerm, accountRole);
  const canEdit = canEditKnowledgeBase(effectivePerm, accountRole);
  const canDelete = canDeleteKnowledgeBase(effectivePerm, accountRole);
  const canKbExcel = canEdit;

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [allArticles, setAllArticles] = useState<SupportKnowledgeArticleRow[]>(
    [],
  );
  const [materialTypes, setMaterialTypes] = useState<
    Array<{ key: string; label: string }>
  >([]);
  const [variableDefinitionRows, setVariableDefinitionRows] = useState<
    SupportKnowledgeVariableDefinitionRow[]
  >([]);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<
    SupportKnowledgeSearchRow[] | null
  >(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedSearch = useDebouncedValue(search.trim(), 350);
  const loadGenRef = useRef(0);

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    KNOWLEDGE_GENERAL_TAB_ID,
  );
  const [selectedTypeKey, setSelectedTypeKey] = useState("all");
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(
    null,
  );
  const [viewArticle, setViewArticle] =
    useState<SupportKnowledgeArticleRow | null>(null);
  const [viewArticleLoading, setViewArticleLoading] = useState(false);
  const articleHistory = useRef<SupportKnowledgeArticleRow[]>([]);
  const currentArticleRef = useRef<SupportKnowledgeArticleRow | null>(null);
  const articleRequest = useRef(0);

  const [formOpen, setFormOpen] = useState(false);
  const [formEditorKey, setFormEditorKey] = useState(0);
  const [formScopeId, setFormScopeId] = useState<string>(
    KNOWLEDGE_GENERAL_TAB_ID,
  );
  const [editArticle, setEditArticle] =
    useState<SupportKnowledgeArticleRow | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formType, setFormType] =
    useState<SupportKnowledgeMaterialType>("other");
  const [formTags, setFormTags] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [formAttachments, setFormAttachments] = useState<
    SupportKnowledgeAttachmentRow[]
  >([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [similarItems, setSimilarItems] = useState<
    SupportKnowledgeSimilarItem[]
  >([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<
    "materials" | "readStats" | "training"
  >("materials");
  const [readStats, setReadStats] =
    useState<SupportKnowledgeReadStatsResponse | null>(null);
  const [readStatsLoading, setReadStatsLoading] = useState(false);
  const [readOperatorsPage, setReadOperatorsPage] = useState(1);
  const [readHistoryPage, setReadHistoryPage] = useState(1);
  const [trainingProjectId, setTrainingProjectId] = useState<string>("");
  const [trainingStats, setTrainingStats] =
    useState<SupportKnowledgeTrainingStatsResponse | null>(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingQuestionsPage, setTrainingQuestionsPage] = useState(1);
  const [trainingAttemptsPage, setTrainingAttemptsPage] = useState(1);
  const [editingQuestion, setEditingQuestion] =
    useState<SupportKnowledgeTrainingQuestionRow | null>(null);
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] =
    useState<SupportKnowledgeTrainingQuestionType>("single_choice");
  const [questionOptions, setQuestionOptions] = useState("Да\nНет");
  const [questionCorrect, setQuestionCorrect] = useState("");
  const [questionPoints, setQuestionPoints] = useState<number | string>(1);
  const [questionExplanation, setQuestionExplanation] = useState("");
  const [questionActive, setQuestionActive] = useState(true);
  const [questionOrder, setQuestionOrder] = useState<number | string>(0);
  const [questionSaving, setQuestionSaving] = useState(false);
  const [reviewTarget, setReviewTarget] =
    useState<SupportKnowledgeTrainingAttemptRow | null>(null);
  const [reviewStatus, setReviewStatus] = useState<"passed" | "failed">(
    "passed",
  );
  const [reviewScore, setReviewScore] = useState<number | string>(0);
  const [reviewComment, setReviewComment] = useState("");
  const [variableProjectId, setVariableProjectId] = useState<string>("");
  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [editingVariable, setEditingVariable] =
    useState<SupportKnowledgeVariableDefinitionRow | null>(null);
  const [variableKey, setVariableKey] = useState("");
  const [variableDefinition, setVariableDefinition] = useState("");
  const [variableActive, setVariableActive] = useState(true);
  const [variableOrder, setVariableOrder] = useState<number | string>(0);
  const [variableSaving, setVariableSaving] = useState(false);
  const [variablesPage, setVariablesPage] = useState(1);
  const [knowledgeTags, setKnowledgeTags] = useState<SupportKnowledgeTagRow[]>(
    [],
  );
  const [knowledgeTagRules, setKnowledgeTagRules] = useState<
    SupportKnowledgeTagRuleRow[]
  >([]);
  const [tagProjectId, setTagProjectId] = useState<string>("");
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<SupportKnowledgeTagRow | null>(
    null,
  );
  const [tagName, setTagName] = useState("");
  const [tagDescription, setTagDescription] = useState("");
  const [tagColor, setTagColor] = useState("#B0E0E6");
  const [tagActive, setTagActive] = useState(true);
  const [tagOrder, setTagOrder] = useState<number | string>(0);
  const [tagSaving, setTagSaving] = useState(false);
  const [tagsPage, setTagsPage] = useState(1);
  const [tagRuleModalOpen, setTagRuleModalOpen] = useState(false);
  const [editingTagRule, setEditingTagRule] =
    useState<SupportKnowledgeTagRuleRow | null>(null);
  const [tagRuleTagId, setTagRuleTagId] = useState("");
  const [tagRuleName, setTagRuleName] = useState("");
  const [tagRuleKeywords, setTagRuleKeywords] = useState("");
  const [tagRuleDescription, setTagRuleDescription] = useState("");
  const [tagRuleActive, setTagRuleActive] = useState(true);
  const [tagRulePriority, setTagRulePriority] = useState<number | string>(0);
  const [tagRuleSaving, setTagRuleSaving] = useState(false);
  const [tagRulesPage, setTagRulesPage] = useState(1);
  const debouncedFormTitle = useDebouncedValue(formTitle.trim(), 450);
  const debouncedFormContent = useDebouncedValue(formContent.trim(), 450);
  const articleTitleOptions = useMemo(
    () =>
      allArticles
        .map((article) => article.title)
        .filter((title) => title !== formTitle)
        .sort((a, b) => a.localeCompare(b, "ru")),
    [allArticles, formTitle],
  );

  const loadAll = useCallback(async () => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    const done = () => {
      if (gen === loadGenRef.current) setLoading(false);
    };
    try {
      const [
        projectList,
        types,
        fetchedArticles,
        fetchedVariableDefinitions,
        fetchedTags,
        fetchedTagRules,
      ] = await Promise.all([
        api.projects(),
        api
          .supportKnowledgeMaterialTypes()
          .catch(() => [] as Array<{ key: string; label: string }>),
        api
          .supportKnowledgeList({ include_inactive: canEdit })
          .catch(() => [] as SupportKnowledgeArticleRow[]),
        api
          .supportKnowledgeVariables({ include_inactive: canEdit })
          .catch(() => [] as SupportKnowledgeVariableDefinitionRow[]),
        api
          .supportKnowledgeTags({ include_inactive: canEdit })
          .catch(() => [] as SupportKnowledgeTagRow[]),
        api
          .supportKnowledgeTagRules({ include_inactive: canEdit })
          .catch(() => [] as SupportKnowledgeTagRuleRow[]),
      ]);
      if (gen !== loadGenRef.current) return;
      const list = Array.isArray(projectList) ? projectList : [];
      list.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      setProjects(list);
      setMaterialTypes(Array.isArray(types) ? types : []);
      setVariableDefinitionRows(
        Array.isArray(fetchedVariableDefinitions)
          ? fetchedVariableDefinitions
          : [],
      );
      setKnowledgeTags(Array.isArray(fetchedTags) ? fetchedTags : []);
      setKnowledgeTagRules(
        Array.isArray(fetchedTagRules) ? fetchedTagRules : [],
      );

      const articles = Array.isArray(fetchedArticles) ? fetchedArticles : [];
      if (gen !== loadGenRef.current) return;
      setAllArticles(articles);
      setSelectedProjectId((prev) => {
        if (isKnowledgeGeneralTab(prev)) return KNOWLEDGE_GENERAL_TAB_ID;
        if (prev && list.some((p) => p.id === prev)) return prev;
        return KNOWLEDGE_GENERAL_TAB_ID;
      });
    } catch (e) {
      if (gen === loadGenRef.current) {
        showNotification({
          title: "Ошибка",
          message:
            e instanceof ApiError ? e.message : "Не удалось загрузить данные",
          color: "red",
        });
        setAllArticles([]);
      }
    } finally {
      done();
    }
  }, [canEdit]);

  const loadReadStats = useCallback(async () => {
    if (!canEdit) return;
    setReadStatsLoading(true);
    try {
      const stats = await api.supportKnowledgeReadStats(300);
      setReadStats(stats);
    } catch (e) {
      showNotification({
        title: "Ошибка",
        message:
          e instanceof ApiError
            ? e.message
            : "Не удалось загрузить статистику ознакомления",
        color: "red",
      });
    } finally {
      setReadStatsLoading(false);
    }
  }, [canEdit]);

  const loadTrainingStats = useCallback(async () => {
    if (!canEdit) return;
    setTrainingLoading(true);
    try {
      const stats = await api.supportKnowledgeTrainingStats(
        trainingProjectId || undefined,
        500,
      );
      setTrainingStats(stats);
    } catch (e) {
      showNotification({
        title: "Ошибка",
        message:
          e instanceof ApiError ? e.message : "Не удалось загрузить обучение",
        color: "red",
      });
    } finally {
      setTrainingLoading(false);
    }
  }, [canEdit, trainingProjectId]);

  const exportSingleArticle = useCallback(
    async (
      _project: ProjectRow | null,
      article: SupportKnowledgeArticleRow,
    ) => {
      void _project;
      try {
        const blob = await api.supportKnowledgeExportArticleJson(article.id);
        downloadBlob(blob, safeMaterialFilename(article));
      } catch (e) {
        showNotification({
          title: "Ошибка выгрузки",
          message:
            e instanceof ApiError ? e.message : "Не удалось выгрузить материал",
          color: "red",
        });
      }
    },
    [],
  );

  const importSingleArticle = useCallback(
    async (
      _project: ProjectRow | null,
      article: SupportKnowledgeArticleRow,
      file: File,
    ) => {
      void _project;
      try {
        const updated = await api.supportKnowledgeImportArticleJson(
          article.id,
          file,
        );
        await loadAll();
        if (selectedArticleId === article.id) {
          setSelectedArticleId(article.id);
          setViewArticle(updated);
        } else if (viewArticle?.id === article.id) {
          setViewArticle(updated);
        }
      } catch (e) {
        showNotification({
          title: "Ошибка импорта",
          message:
            e instanceof ApiError ? e.message : "Не удалось загрузить материал",
          color: "red",
        });
      }
    },
    [loadAll, selectedArticleId, viewArticle?.id],
  );

  useEffect(() => {
    if (!canView) return;
    void loadAll();
    return () => {
      loadGenRef.current++;
    };
  }, [canView, loadAll]);

  useEffect(() => {
    if (activeSection !== "readStats") return;
    void loadReadStats();
  }, [activeSection, loadReadStats]);

  useEffect(() => {
    if (activeSection !== "training") return;
    void loadTrainingStats();
  }, [activeSection, loadTrainingStats]);

  useEffect(() => {
    if (!canView || !debouncedSearch) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    void api
      .supportKnowledgeSearch({ q: debouncedSearch, limit: 80 })
      .then((rows) => {
        if (!cancelled) setSearchResults(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canView, debouncedSearch]);

  const effectiveMaterialTypes = useMemo(() => {
    if (materialTypes.length > 0) {
      return materialTypes as Array<{
        key: SupportKnowledgeMaterialType;
        label: string;
      }>;
    }
    return FALLBACK_MATERIAL_TYPES;
  }, [materialTypes]);

  useEffect(() => {
    if (!formOpen || !(canAdd || canEdit)) {
      setSimilarItems([]);
      return;
    }
    if (debouncedFormTitle.length < 3 && debouncedFormContent.length < 8) {
      setSimilarItems([]);
      return;
    }
    const scopeProjectId = editArticle
      ? (editArticle.project_id ?? null)
      : isKnowledgeGeneralTab(formScopeId)
        ? null
        : formScopeId;
    let cancelled = false;
    setSimilarLoading(true);
    void api
      .supportKnowledgeSimilar({
        project_id: scopeProjectId,
        title: debouncedFormTitle,
        content: stripKnowledgeContent(debouncedFormContent, "html"),
        exclude_id: editArticle?.id,
        limit: 5,
      })
      .then((resp) => {
        if (!cancelled) setSimilarItems(resp.items || []);
      })
      .catch(() => {
        if (!cancelled) setSimilarItems([]);
      })
      .finally(() => {
        if (!cancelled) setSimilarLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formOpen,
    formScopeId,
    canAdd,
    canEdit,
    debouncedFormTitle,
    debouncedFormContent,
    editArticle?.id,
    editArticle?.project_id,
  ]);

  const bundles = useMemo(
    () => groupArticlesByProject(projects, allArticles),
    [projects, allArticles],
  );
  const generalArticles = useMemo(
    () => generalArticlesFromList(allArticles),
    [allArticles],
  );

  const projectById = useMemo(() => {
    const m = new Map<string, ProjectRow>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const readOperatorRows = readStats?.operators ?? [];
  const readHistoryRows = readStats?.history ?? [];
  const trainingQuestionRows = trainingStats?.questions ?? [];
  const trainingAttemptRows = trainingStats?.attempts ?? [];
  const readOperatorPages = knowledgeTablePageCount(readOperatorRows.length);
  const readHistoryPages = knowledgeTablePageCount(readHistoryRows.length);
  const trainingQuestionPages = knowledgeTablePageCount(
    trainingQuestionRows.length,
  );
  const trainingAttemptPages = knowledgeTablePageCount(
    trainingAttemptRows.length,
  );
  const visibleReadOperatorRows = knowledgeTablePageRows(
    readOperatorRows,
    readOperatorsPage,
  );
  const visibleReadHistoryRows = knowledgeTablePageRows(
    readHistoryRows,
    readHistoryPage,
  );
  const visibleTrainingQuestionRows = knowledgeTablePageRows(
    trainingQuestionRows,
    trainingQuestionsPage,
  );
  const visibleTrainingAttemptRows = knowledgeTablePageRows(
    trainingAttemptRows,
    trainingAttemptsPage,
  );
  const variableDefinitionsByProject = useMemo(() => {
    const byProject = new Map<string, Record<string, string>>();
    const general: Record<string, string> = {};
    for (const row of variableDefinitionRows) {
      if (row.is_active === false) continue;
      const key = String(row.key || "").trim();
      const definition = String(row.definition || "").trim();
      if (!key || !definition) continue;
      if (!row.project_id) {
        general[key] = definition;
        continue;
      }
      const current = byProject.get(row.project_id) ?? {};
      current[key] = definition;
      byProject.set(row.project_id, current);
    }
    return { byProject, general };
  }, [variableDefinitionRows]);

  const getVariableDefinitionsForArticle = useCallback(
    (article: SupportKnowledgeArticleRow | null | undefined) => {
      if (!article?.project_id) return variableDefinitionsByProject.general;
      return {
        ...variableDefinitionsByProject.general,
        ...(variableDefinitionsByProject.byProject.get(article.project_id) ??
          {}),
      };
    },
    [variableDefinitionsByProject],
  );

  const variableRows = useMemo(() => {
    const rows = [...variableDefinitionRows];
    const filtered = isKnowledgeGeneralTab(variableProjectId)
      ? rows.filter((row) => !row.project_id)
      : variableProjectId
        ? rows.filter((row) => row.project_id === variableProjectId)
        : rows;
    return filtered.sort((a, b) => {
      const projectDelta = (a.project_name || "").localeCompare(
        b.project_name || "",
        "ru",
      );
      if (projectDelta !== 0) return projectDelta;
      const orderDelta = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (orderDelta !== 0) return orderDelta;
      return a.key.localeCompare(b.key, "ru");
    });
  }, [variableDefinitionRows, variableProjectId]);
  const variablePages = knowledgeTablePageCount(variableRows.length);
  const visibleVariableRows = knowledgeTablePageRows(
    variableRows,
    variablesPage,
  );
  const tagRows = useMemo(() => {
    const filtered = isKnowledgeGeneralTab(tagProjectId)
      ? knowledgeTags.filter((row) => !row.project_id)
      : tagProjectId
        ? knowledgeTags.filter((row) => row.project_id === tagProjectId)
        : knowledgeTags;
    return [...filtered].sort((a, b) => {
      const projectDelta = (a.project_name || "").localeCompare(
        b.project_name || "",
        "ru",
      );
      if (projectDelta !== 0) return projectDelta;
      const orderDelta = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (orderDelta !== 0) return orderDelta;
      return a.name.localeCompare(b.name, "ru");
    });
  }, [knowledgeTags, tagProjectId]);
  const tagRuleRows = useMemo(() => {
    const filtered = isKnowledgeGeneralTab(tagProjectId)
      ? knowledgeTagRules.filter((row) => !row.project_id)
      : tagProjectId
        ? knowledgeTagRules.filter((row) => row.project_id === tagProjectId)
        : knowledgeTagRules;
    return [...filtered].sort((a, b) => {
      const projectDelta = (a.project_name || "").localeCompare(
        b.project_name || "",
        "ru",
      );
      if (projectDelta !== 0) return projectDelta;
      const priorityDelta = (b.priority ?? 0) - (a.priority ?? 0);
      if (priorityDelta !== 0) return priorityDelta;
      return (a.name || a.tag_name || "").localeCompare(
        b.name || b.tag_name || "",
        "ru",
      );
    });
  }, [knowledgeTagRules, tagProjectId]);
  const tagPages = knowledgeTablePageCount(tagRows.length);
  const tagRulePages = knowledgeTablePageCount(tagRuleRows.length);
  const visibleTagRows = knowledgeTablePageRows(tagRows, tagsPage);
  const visibleTagRuleRows = knowledgeTablePageRows(tagRuleRows, tagRulesPage);
  const tagOptions = useMemo(() => {
    return knowledgeTags
      .filter((row) => {
        if (isKnowledgeGeneralTab(tagProjectId)) return !row.project_id;
        if (tagProjectId) return row.project_id === tagProjectId;
        return true;
      })
      .map((row) => ({
        value: row.id,
        label: `${row.name} · ${row.project_id ? row.project_name || "Проект" : "Общие"}`,
      }));
  }, [knowledgeTags, tagProjectId]);

  useEffect(() => {
    setVariablesPage((page) => Math.min(page, variablePages));
  }, [variablePages]);

  useEffect(() => {
    setTagsPage((page) => Math.min(page, tagPages));
  }, [tagPages]);

  useEffect(() => {
    setTagRulesPage((page) => Math.min(page, tagRulePages));
  }, [tagRulePages]);

  useEffect(() => {
    setReadOperatorsPage((page) => Math.min(page, readOperatorPages));
  }, [readOperatorPages]);

  useEffect(() => {
    setReadHistoryPage((page) => Math.min(page, readHistoryPages));
  }, [readHistoryPages]);

  useEffect(() => {
    setTrainingQuestionsPage((page) => Math.min(page, trainingQuestionPages));
  }, [trainingQuestionPages]);

  useEffect(() => {
    setTrainingAttemptsPage((page) => Math.min(page, trainingAttemptPages));
  }, [trainingAttemptPages]);

  const totalArticles = useMemo(
    () =>
      generalArticles.length +
      bundles.reduce((n, b) => n + b.articles.length, 0),
    [generalArticles, bundles],
  );

  const openCreate = (
    project?: ProjectRow | null,
    materialType?: SupportKnowledgeMaterialType,
  ) => {
    const fromGeneral = isKnowledgeGeneralTab(selectedProjectId);
    const scopeId =
      project?.id ??
      (fromGeneral
        ? KNOWLEDGE_GENERAL_TAB_ID
        : selectedProjectId || KNOWLEDGE_GENERAL_TAB_ID);
    setFormScopeId(scopeId);
    setEditArticle(null);
    setFormTitle("");
    setFormContent("");
    setFormType(
      materialType ??
        (selectedTypeKey !== "all"
          ? (selectedTypeKey as SupportKnowledgeMaterialType)
          : "other"),
    );
    setFormTags("");
    setFormActive(true);
    setFormAttachments([]);
    setPendingFiles([]);
    setSimilarItems([]);
    setFormEditorKey((k) => k + 1);
    setFormOpen(true);
  };

  const loadVariables = useCallback(async () => {
    if (!canEdit) return;
    const rows = await api.supportKnowledgeVariables({
      include_inactive: true,
    });
    setVariableDefinitionRows(Array.isArray(rows) ? rows : []);
  }, [canEdit]);

  const openCreateVariable = (projectId?: string | null) => {
    setEditingVariable(null);
    setVariableProjectId(
      projectId ??
        (isKnowledgeGeneralTab(selectedProjectId)
          ? KNOWLEDGE_GENERAL_TAB_ID
          : variableProjectId || projects[0]?.id || ""),
    );
    setVariableKey("");
    setVariableDefinition("");
    setVariableActive(true);
    setVariableOrder(0);
    setVariableModalOpen(true);
  };

  const openEditVariable = (row: SupportKnowledgeVariableDefinitionRow) => {
    setEditingVariable(row);
    setVariableProjectId(row.project_id || KNOWLEDGE_GENERAL_TAB_ID);
    setVariableKey(row.key || "");
    setVariableDefinition(row.definition || "");
    setVariableActive(row.is_active !== false);
    setVariableOrder(row.sort_order ?? 0);
    setVariableModalOpen(true);
  };

  const saveVariable = async () => {
    const key = variableKey
      .trim()
      .replace(/^\[+|\]+$/g, "")
      .replace(/\s+/g, "_");
    const definition = variableDefinition.trim();
    if (!key || !definition) {
      showNotification({
        title: "Переменная",
        message: "Заполните ключ и определение переменной",
        color: "yellow",
      });
      return;
    }
    const order =
      typeof variableOrder === "number"
        ? variableOrder
        : parseInt(String(variableOrder), 10);
    const payload: SupportKnowledgeVariableWritePayload = {
      project_id: isKnowledgeGeneralTab(variableProjectId)
        ? null
        : variableProjectId || null,
      key,
      definition,
      is_active: variableActive,
      sort_order: Number.isFinite(order) ? order : 0,
    };
    setVariableSaving(true);
    try {
      if (editingVariable) {
        await api.supportKnowledgeVariableUpdate(editingVariable.id, payload);
      } else {
        await api.supportKnowledgeVariableCreate(payload);
      }
      setVariableModalOpen(false);
      await loadVariables();
      showNotification({ title: "Сохранено", message: "", color: "green" });
    } catch (e) {
      showNotification({
        title: "Ошибка",
        message:
          e instanceof ApiError ? e.message : "Не удалось сохранить переменную",
        color: "red",
      });
    } finally {
      setVariableSaving(false);
    }
  };

  const confirmDeleteVariable = (
    row: SupportKnowledgeVariableDefinitionRow,
  ) => {
    openConfirmModal({
      title: "Удалить переменную?",
      children: (
        <Text size="sm">
          Переменная [{row.key}] будет удалена из базы знаний. Материалы и
          инструкции не изменятся.
        </Text>
      ),
      labels: { confirm: "Удалить", cancel: "Отмена" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        void api
          .supportKnowledgeVariableDelete(row.id)
          .then(() => loadVariables())
          .catch((e) =>
            showNotification({
              title: "Ошибка",
              message:
                e instanceof ApiError
                  ? e.message
                  : "Не удалось удалить переменную",
              color: "red",
            }),
          ),
    });
  };

  const loadKnowledgeTags = useCallback(async () => {
    if (!canEdit) return;
    const [tags, rules] = await Promise.all([
      api.supportKnowledgeTags({ include_inactive: true }),
      api.supportKnowledgeTagRules({ include_inactive: true }),
    ]);
    setKnowledgeTags(Array.isArray(tags) ? tags : []);
    setKnowledgeTagRules(Array.isArray(rules) ? rules : []);
  }, [canEdit]);

  const openCreateTag = (projectId?: string | null) => {
    setEditingTag(null);
    setTagProjectId(
      projectId ??
        (isKnowledgeGeneralTab(selectedProjectId)
          ? KNOWLEDGE_GENERAL_TAB_ID
          : tagProjectId || projects[0]?.id || ""),
    );
    setTagName("");
    setTagDescription("");
    setTagColor("#B0E0E6");
    setTagActive(true);
    setTagOrder(0);
    setTagModalOpen(true);
  };

  const openEditTag = (row: SupportKnowledgeTagRow) => {
    setEditingTag(row);
    setTagProjectId(row.project_id || KNOWLEDGE_GENERAL_TAB_ID);
    setTagName(row.name || "");
    setTagDescription(row.description || "");
    setTagColor(row.color || "#B0E0E6");
    setTagActive(row.is_active !== false);
    setTagOrder(row.sort_order ?? 0);
    setTagModalOpen(true);
  };

  const saveTag = async () => {
    const name = tagName.trim();
    if (!name) {
      showNotification({
        title: "Метка",
        message: "Укажите название метки",
        color: "yellow",
      });
      return;
    }
    const order =
      typeof tagOrder === "number" ? tagOrder : parseInt(String(tagOrder), 10);
    const payload: SupportKnowledgeTagWritePayload = {
      project_id: isKnowledgeGeneralTab(tagProjectId)
        ? null
        : tagProjectId || null,
      name,
      description: tagDescription.trim(),
      color: tagColor || "#B0E0E6",
      is_active: tagActive,
      sort_order: Number.isFinite(order) ? order : 0,
    };
    setTagSaving(true);
    try {
      if (editingTag) {
        await api.supportKnowledgeTagUpdate(editingTag.id, payload);
      } else {
        await api.supportKnowledgeTagCreate(payload);
      }
      setTagModalOpen(false);
      await loadKnowledgeTags();
      showNotification({ title: "Сохранено", message: "", color: "green" });
    } catch (e) {
      showNotification({
        title: "Ошибка",
        message:
          e instanceof ApiError ? e.message : "Не удалось сохранить метку",
        color: "red",
      });
    } finally {
      setTagSaving(false);
    }
  };

  const confirmDeleteTag = (row: SupportKnowledgeTagRow) => {
    openConfirmModal({
      title: "Удалить метку?",
      children: (
        <Text size="sm">
          Метка «{row.name}» и её правила будут удалены только из базы знаний.
          Текущие рабочие метки проектов не изменятся.
        </Text>
      ),
      labels: { confirm: "Удалить", cancel: "Отмена" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        void api
          .supportKnowledgeTagDelete(row.id)
          .then(() => loadKnowledgeTags())
          .catch((e) =>
            showNotification({
              title: "Ошибка",
              message:
                e instanceof ApiError ? e.message : "Не удалось удалить метку",
              color: "red",
            }),
          ),
    });
  };

  const openCreateTagRule = (projectId?: string | null) => {
    const scopeProjectId =
      projectId ??
      (isKnowledgeGeneralTab(selectedProjectId)
        ? KNOWLEDGE_GENERAL_TAB_ID
        : tagProjectId);
    const firstTag =
      knowledgeTags.find((row) =>
        isKnowledgeGeneralTab(scopeProjectId)
          ? !row.project_id
          : row.project_id === scopeProjectId,
      )?.id ||
      tagOptions[0]?.value ||
      "";
    setEditingTagRule(null);
    if (scopeProjectId) setTagProjectId(scopeProjectId);
    setTagRuleTagId(firstTag);
    setTagRuleName("");
    setTagRuleKeywords("");
    setTagRuleDescription("");
    setTagRuleActive(true);
    setTagRulePriority(0);
    setTagRuleModalOpen(true);
  };

  const openEditTagRule = (row: SupportKnowledgeTagRuleRow) => {
    setEditingTagRule(row);
    setTagProjectId(row.project_id || KNOWLEDGE_GENERAL_TAB_ID);
    setTagRuleTagId(row.tag_id);
    setTagRuleName(row.name || "");
    setTagRuleKeywords(row.keywords || "");
    setTagRuleDescription(row.description || "");
    setTagRuleActive(row.is_active !== false);
    setTagRulePriority(row.priority ?? 0);
    setTagRuleModalOpen(true);
  };

  const saveTagRule = async () => {
    if (!tagRuleTagId) {
      showNotification({
        title: "Правило",
        message: "Выберите метку для правила",
        color: "yellow",
      });
      return;
    }
    const tag = knowledgeTags.find((row) => row.id === tagRuleTagId);
    if (!tag) return;
    const priority =
      typeof tagRulePriority === "number"
        ? tagRulePriority
        : parseInt(String(tagRulePriority), 10);
    const payload: SupportKnowledgeTagRuleWritePayload = {
      project_id: tag.project_id ?? null,
      tag_id: tagRuleTagId,
      name: tagRuleName.trim(),
      keywords: tagRuleKeywords.trim(),
      description: tagRuleDescription.trim(),
      is_active: tagRuleActive,
      priority: Number.isFinite(priority) ? priority : 0,
    };
    setTagRuleSaving(true);
    try {
      if (editingTagRule) {
        await api.supportKnowledgeTagRuleUpdate(editingTagRule.id, payload);
      } else {
        await api.supportKnowledgeTagRuleCreate(payload);
      }
      setTagRuleModalOpen(false);
      await loadKnowledgeTags();
      showNotification({ title: "Сохранено", message: "", color: "green" });
    } catch (e) {
      showNotification({
        title: "Ошибка",
        message:
          e instanceof ApiError ? e.message : "Не удалось сохранить правило",
        color: "red",
      });
    } finally {
      setTagRuleSaving(false);
    }
  };

  const confirmDeleteTagRule = (row: SupportKnowledgeTagRuleRow) => {
    openConfirmModal({
      title: "Удалить правило метки?",
      children: (
        <Text size="sm">
          Правило «{row.name || row.tag_name || "без названия"}» будет удалено
          только из базы знаний.
        </Text>
      ),
      labels: { confirm: "Удалить", cancel: "Отмена" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        void api
          .supportKnowledgeTagRuleDelete(row.id)
          .then(() => loadKnowledgeTags())
          .catch((e) =>
            showNotification({
              title: "Ошибка",
              message:
                e instanceof ApiError
                  ? e.message
                  : "Не удалось удалить правило",
              color: "red",
            }),
          ),
    });
  };

  const selectArticle = useCallback(
    async (article: SupportKnowledgeArticleRow | null, back = false) => {
      const request = ++articleRequest.current;
      if (!article) {
        articleHistory.current = [];
        currentArticleRef.current = null;
        setSelectedArticleId(null);
        setViewArticle(null);
        setViewArticleLoading(false);
        return;
      }
      if (!back && currentArticleRef.current && currentArticleRef.current.id !== article.id) articleHistory.current.push(currentArticleRef.current);
      currentArticleRef.current = article;
      setSelectedArticleId(article.id);
      setViewArticle(article);
      setViewArticleLoading(true);
      try {
        const full = await api.supportKnowledgeGet(article.id);
        if (request !== articleRequest.current) return;
        setViewArticle(full);
      } catch (e) {
        if (request !== articleRequest.current) return;
        showNotification({
          title: "Ошибка",
          message:
            "Материал удалён или недоступен. Вернитесь к предыдущему материалу.",
          color: "red",
        });
      } finally {
        if (request === articleRequest.current) setViewArticleLoading(false);
      }
    },
    [],
  );

  const openEdit = async (
    _project: ProjectRow | null,
    article: SupportKnowledgeArticleRow,
  ) => {
    let full = article;
    if (!article.content && article.id) {
      try {
        full = await api.supportKnowledgeGet(article.id);
      } catch (e) {
        showNotification({
          title: "Ошибка",
          message:
            e instanceof ApiError ? e.message : "Не удалось загрузить материал",
          color: "red",
        });
        return;
      }
    }
    setFormScopeId(article.project_id ?? KNOWLEDGE_GENERAL_TAB_ID);
    setEditArticle(full);
    setFormTitle(full.title || "");
    setFormContent(full.content || "");
    setFormType(full.material_type || "other");
    setFormTags((full.tags || []).join(", "));
    setFormActive(full.is_active !== false);
    setFormAttachments(full.attachments || []);
    setPendingFiles([]);
    setSimilarItems([]);
    setFormEditorKey((k) => k + 1);
    setFormOpen(true);
  };

  const submitForm = async () => {
    const title = formTitle.trim();
    if (!title) {
      showNotification({
        title: "Заголовок",
        message: "Укажите название материала.",
        color: "yellow",
      });
      return;
    }
    if (
      !editArticle &&
      !isKnowledgeGeneralTab(formScopeId) &&
      !projects.some((p) => p.id === formScopeId)
    ) {
      showNotification({
        title: "Раздел",
        message: "Выберите раздел для материала.",
        color: "yellow",
      });
      return;
    }
    const tags = formTags
      .split(/[,;\n]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      if (editArticle) {
        await api.supportKnowledgeUpdate(editArticle.id, {
          title,
          content: formContent,
          content_format: "html",
          material_type: formType,
          tags,
          is_active: formActive,
        });
      } else {
        const created = await api.supportKnowledgeCreate({
          project_id: isKnowledgeGeneralTab(formScopeId) ? null : formScopeId,
          title,
          content: formContent,
          content_format: "html",
          material_type: formType,
          tags,
          is_active: formActive,
        });
        if (pendingFiles.length > 0) {
          await flushPendingKnowledgeAttachments(created.id, pendingFiles);
        }
      }
      setFormOpen(false);
      await loadAll();
      if (activeSection === "readStats") void loadReadStats();
      showNotification({ title: "Сохранено", message: "", color: "green" });
    } catch (e) {
      showNotification({
        title: "Ошибка",
        message: e instanceof ApiError ? e.message : "Не удалось сохранить",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (
    project: ProjectRow | null,
    article: SupportKnowledgeArticleRow,
  ) => {
    const scopeLabel = project?.name ?? "Общие";
    openConfirmModal({
      title: "Удалить материал?",
      children: (
        <Text size="sm">
          «{article.title}» будет удалён из базы знаний ({scopeLabel}).
          {" "}Блок: «{article.material_type_label}». Будет удалён 1 материал.
          Остальные материалы не будут затронуты.
        </Text>
      ),
      labels: { confirm: "Удалить", cancel: "Отмена" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await api.supportKnowledgeDelete(article.id);
          setSearch("");
          setSearchResults(null);
          if (viewArticle?.id === article.id) void selectArticle(null);
          await loadAll();
          if (activeSection === "readStats") void loadReadStats();
          showNotification({ title: "Удалено", message: "", color: "green" });
        } catch (e) {
          showNotification({
            title: "Ошибка",
            message: e instanceof ApiError ? e.message : "Не удалось удалить",
            color: "red",
          });
        }
      },
    });
  };

  const resetQuestionForm = () => {
    setEditingQuestion(null);
    setQuestionText("");
    setQuestionType("single_choice");
    setQuestionOptions("Да\nНет");
    setQuestionCorrect("");
    setQuestionPoints(1);
    setQuestionExplanation("");
    setQuestionActive(true);
    setQuestionOrder(0);
  };

  const openQuestionEdit = (row: SupportKnowledgeTrainingQuestionRow) => {
    setEditingQuestion(row);
    setTrainingProjectId(row.project_id);
    setQuestionText(row.question_text);
    setQuestionType(row.question_type);
    setQuestionOptions((row.options || []).join("\n"));
    setQuestionCorrect((row.correct_answers || []).join("\n"));
    setQuestionPoints(row.points);
    setQuestionExplanation(row.explanation || "");
    setQuestionActive(row.is_active);
    setQuestionOrder(row.display_order);
  };

  const saveTrainingQuestion = async () => {
    if (!trainingProjectId) {
      showNotification({
        title: "Проект",
        message: "Выберите проект для вопроса",
        color: "yellow",
      });
      return;
    }
    const text = questionText.trim();
    if (!text) {
      showNotification({
        title: "Вопрос",
        message: "Введите текст вопроса",
        color: "yellow",
      });
      return;
    }
    const options = questionOptions
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);
    const correct = questionCorrect
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);
    const points =
      typeof questionPoints === "number"
        ? questionPoints
        : parseInt(String(questionPoints), 10);
    const order =
      typeof questionOrder === "number"
        ? questionOrder
        : parseInt(String(questionOrder), 10);
    setQuestionSaving(true);
    try {
      const payload = {
        project_id: trainingProjectId,
        question_text: text,
        question_type: questionType,
        options: questionType === "free_text" ? null : options,
        correct_answers: questionType === "free_text" ? null : correct,
        points: Number.isFinite(points) ? points : 1,
        explanation: questionExplanation.trim() || null,
        is_active: questionActive,
        display_order: Number.isFinite(order) ? order : 0,
      };
      if (editingQuestion) {
        await api.supportKnowledgeTrainingUpdateQuestion(
          editingQuestion.id,
          payload,
        );
      } else {
        await api.supportKnowledgeTrainingCreateQuestion(payload);
      }
      resetQuestionForm();
      await loadTrainingStats();
      showNotification({ title: "Сохранено", message: "", color: "green" });
    } catch (e) {
      showNotification({
        title: "Ошибка",
        message:
          e instanceof ApiError ? e.message : "Не удалось сохранить вопрос",
        color: "red",
      });
    } finally {
      setQuestionSaving(false);
    }
  };

  const deleteTrainingQuestion = async (
    row: SupportKnowledgeTrainingQuestionRow,
  ) => {
    openConfirmModal({
      title: "Удалить вопрос?",
      children: <Text size="sm">Вопрос будет удалён из проверки знаний.</Text>,
      labels: { confirm: "Удалить", cancel: "Отмена" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        await api.supportKnowledgeTrainingDeleteQuestion(row.id);
        await loadTrainingStats();
      },
    });
  };

  const reviewAttempt = async () => {
    if (!reviewTarget) return;
    const score =
      typeof reviewScore === "number"
        ? reviewScore
        : parseInt(String(reviewScore), 10);
    await api.supportKnowledgeTrainingReviewAttempt(reviewTarget.id, {
      status: reviewStatus,
      score: Number.isFinite(score) ? score : reviewTarget.score,
      reviewer_comment: reviewComment.trim() || null,
    });
    setReviewTarget(null);
    setReviewComment("");
    await loadTrainingStats();
  };

  if (!canView) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading && allArticles.length === 0 && projects.length === 0) {
    return (
      <PageShell>
        <PageCenterLoader />
      </PageShell>
    );
  }

  const mobileTypeNav = !isWide && !debouncedSearch;

  return (
    <PageShell variant="fill">
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Group gap="xs">
            <IconBook size={22} stroke={1.5} />
            <Text fw={600} size="lg">
              База знаний
            </Text>
            <Badge variant="light" color="gray">
              {articleCountLabel(totalArticles)}
            </Badge>
          </Group>
          <Group gap="sm">
            <Button
              leftSection={<IconBook size={16} stroke={1.5} />}
              variant="light"
              onClick={() => setGuideOpen(true)}
            >
              Гайд
            </Button>
            {canKbExcel ? (
              <Button
                leftSection={<IconFile size={16} stroke={1.5} />}
                variant="default"
                onClick={() => setExcelOpen(true)}
              >
                Excel
              </Button>
            ) : null}
            {canAdd ? (
              <Button
                leftSection={<IconPlus size={16} stroke={1.5} />}
                onClick={() => openCreate()}
              >
                Добавить
              </Button>
            ) : null}
            <Button
              leftSection={<IconRefresh size={16} stroke={1.5} />}
              variant="light"
              onClick={() => void loadAll()}
              disabled={loading}
            >
              Обновить
            </Button>
          </Group>
        </Group>

        <Tabs
          value={activeSection}
          onChange={(value) => {
            if (
              value === "readStats" ||
              value === "training" ||
              value === "materials"
            ) {
              setActiveSection(value);
            }
          }}
          keepMounted={false}
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Tabs.List>
            <Tabs.Tab
              value="materials"
              leftSection={<IconBook size={14} stroke={1.5} />}
            >
              Материалы
            </Tabs.Tab>
            {canEdit ? (
              <Tabs.Tab
                value="readStats"
                leftSection={<IconEye size={14} stroke={1.5} />}
              >
                Ознакомление
              </Tabs.Tab>
            ) : null}
            {canEdit ? (
              <Tabs.Tab
                value="training"
                leftSection={<IconCheck size={14} stroke={1.5} />}
              >
                Обучение
              </Tabs.Tab>
            ) : null}
          </Tabs.List>

          <Tabs.Panel
            value="materials"
            pt="sm"
            style={{ flex: 1, minHeight: 0 }}
          >
            <Stack gap="sm" style={{ height: "100%", minHeight: 0 }}>
              <TextInput
                placeholder="Семантический поиск: оплата, возврат, как подключить VPN…"
                leftSection={<IconSearch size={16} stroke={1.5} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  if (!e.currentTarget.value.trim()) void selectArticle(null);
                }}
                rightSection={
                  searchLoading ? (
                    <Text size="xs" c="dimmed">
                      …
                    </Text>
                  ) : null
                }
              />

              {!debouncedSearch ? (
                <KnowledgeProjectTabs
                  bundles={bundles}
                  generalCount={generalArticles.length}
                  value={selectedProjectId}
                  onChange={(id) => {
                    setSelectedProjectId(id);
                    void selectArticle(null);
                  }}
                />
              ) : null}

              {mobileTypeNav ? (
                <NativeSelect
                  label="Раздел"
                  value={selectedTypeKey}
                  onChange={(e) => {
                    setSelectedTypeKey(e.currentTarget.value);
                    void selectArticle(null);
                  }}
                  data={[
                    { value: "all", label: "Все типы" },
                    ...effectiveMaterialTypes.map((t) => ({
                      value: t.key,
                      label: t.label,
                    })),
                    {
                      value: KNOWLEDGE_VARIABLES_SECTION,
                      label: "Переменные",
                    },
                    { value: KNOWLEDGE_TAGS_SECTION, label: "Метки" },
                    {
                      value: KNOWLEDGE_TAG_RULES_SECTION,
                      label: "Правила меток",
                    },
                  ]}
                />
              ) : null}

              {canDelete && <KnowledgeDeleteControls
                projectId={isKnowledgeGeneralTab(selectedProjectId) ? null : selectedProjectId}
                projectName={isKnowledgeGeneralTab(selectedProjectId) ? "Общие" : projectById.get(selectedProjectId || "")?.name || "Проект"}
                types={effectiveMaterialTypes}
                onDeleted={async () => { await selectArticle(null); setSearch(""); setSearchResults(null); await loadAll(); }}
              />}
              {articleHistory.current.length > 0 && <Button variant="subtle" onClick={() => { const previous = articleHistory.current.pop(); if (previous) void selectArticle(previous, true); }}>Назад к предыдущему материалу</Button>}
              <KnowledgeBaseWorkspace
                generalArticles={generalArticles}
                bundles={bundles}
                materialTypes={effectiveMaterialTypes}
                searchResults={searchResults}
                debouncedSearch={debouncedSearch}
                loading={loading}
                canAdd={canAdd}
                canEdit={canEdit}
                canDelete={canDelete}
                selectedProjectId={selectedProjectId}
                selectedTypeKey={selectedTypeKey}
                onSelectType={setSelectedTypeKey}
                selectedArticleId={selectedArticleId}
                onSelectArticle={(a) => void selectArticle(a)}
                viewArticle={viewArticle}
                viewArticleLoading={viewArticleLoading}
                onOpenCreate={openCreate}
                onOpenEdit={(p, a) => void openEdit(p, a)}
                onDelete={confirmDelete}
                onExportArticle={exportSingleArticle}
                onImportArticle={importSingleArticle}
                projectById={projectById}
                getVariableDefinitions={getVariableDefinitionsForArticle}
                variables={variableDefinitionRows}
                tags={knowledgeTags}
                tagRules={knowledgeTagRules}
                onOpenCreateVariable={openCreateVariable}
                onOpenEditVariable={openEditVariable}
                onDeleteVariable={confirmDeleteVariable}
                onOpenCreateTag={openCreateTag}
                onOpenEditTag={openEditTag}
                onDeleteTag={confirmDeleteTag}
                onOpenCreateTagRule={openCreateTagRule}
                onOpenEditTagRule={openEditTagRule}
                onDeleteTagRule={confirmDeleteTagRule}
              />
            </Stack>
          </Tabs.Panel>

          {canEdit ? (
            <Tabs.Panel
              value="variables"
              pt="sm"
              style={{ flex: 1, minHeight: 0 }}
            >
              <ScrollArea h="100%" type="auto" offsetScrollbars>
                <Stack gap="md" pb="md">
                  <Group justify="space-between" align="flex-end" wrap="wrap">
                    <NativeSelect
                      label="Раздел"
                      value={variableProjectId}
                      onChange={(e) => {
                        setVariableProjectId(e.currentTarget.value);
                        setVariablesPage(1);
                      }}
                      data={[
                        { value: "", label: "Все разделы" },
                        ...projects.map((p) => ({
                          value: p.id,
                          label: p.name || p.slug || p.id,
                        })),
                      ]}
                    />
                    <Group gap="sm">
                      <Button
                        leftSection={<IconPlus size={16} stroke={1.5} />}
                        onClick={() => openCreateVariable()}
                      >
                        Добавить переменную
                      </Button>
                      <Button
                        leftSection={<IconRefresh size={16} stroke={1.5} />}
                        variant="light"
                        onClick={() => void loadVariables()}
                      >
                        Обновить
                      </Button>
                    </Group>
                  </Group>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Group justify="space-between" align="center">
                        <Stack gap={2}>
                          <Text fw={600}>Переменные базы знаний</Text>
                          <Text size="sm" c="dimmed">
                            Определения используются во всплывающих подсказках
                            материалов БЗ.
                          </Text>
                        </Stack>
                        <Badge variant="light" color="gray">
                          {variableRows.length}
                        </Badge>
                      </Group>
                      <ScrollArea type="auto" offsetScrollbars>
                        <Table
                          striped
                          highlightOnHover
                          verticalSpacing="sm"
                          miw={920}
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Ключ</Table.Th>
                              <Table.Th>Определение</Table.Th>
                              <Table.Th>Раздел</Table.Th>
                              <Table.Th>Порядок</Table.Th>
                              <Table.Th>Статус</Table.Th>
                              <Table.Th />
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {visibleVariableRows.map((row) => (
                              <Table.Tr key={row.id}>
                                <Table.Td>
                                  <Text size="sm" ff="monospace" fw={600}>
                                    [{row.key}]
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" lineClamp={3}>
                                    {row.definition}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  {row.project_id
                                    ? row.project_name || "Проект"
                                    : "Проект не выбран"}
                                </Table.Td>
                                <Table.Td>{row.sort_order ?? 0}</Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={row.is_active ? "green" : "gray"}
                                    variant="light"
                                  >
                                    {row.is_active ? "активна" : "выключена"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs" justify="flex-end">
                                    <Button
                                      size="compact-sm"
                                      variant="light"
                                      onClick={() => openEditVariable(row)}
                                    >
                                      Изменить
                                    </Button>
                                    <Button
                                      size="compact-sm"
                                      color="red"
                                      variant="light"
                                      onClick={() => confirmDeleteVariable(row)}
                                    >
                                      Удалить
                                    </Button>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                      {variableRows.length > KNOWLEDGE_TABLE_PAGE_SIZE ? (
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            Всего: {variableRows.length}
                          </Text>
                          <PaginationBar
                            page={variablesPage}
                            pageCount={variablePages}
                            onPageChange={setVariablesPage}
                          />
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
          ) : null}

          {canEdit ? (
            <Tabs.Panel value="tags" pt="sm" style={{ flex: 1, minHeight: 0 }}>
              <ScrollArea h="100%" type="auto" offsetScrollbars>
                <Stack gap="md" pb="md">
                  <Group justify="space-between" align="flex-end" wrap="wrap">
                    <NativeSelect
                      label="Раздел"
                      value={tagProjectId}
                      onChange={(e) => {
                        setTagProjectId(e.currentTarget.value);
                        setTagsPage(1);
                        setTagRulesPage(1);
                      }}
                      data={[
                        { value: "", label: "Все разделы" },
                        ...projects.map((p) => ({
                          value: p.id,
                          label: p.name || p.slug || p.id,
                        })),
                      ]}
                    />
                    <Group gap="sm">
                      <Button
                        leftSection={<IconPlus size={16} stroke={1.5} />}
                        onClick={() => openCreateTag()}
                      >
                        Добавить метку
                      </Button>
                      <Button
                        leftSection={<IconPlus size={16} stroke={1.5} />}
                        variant="light"
                        onClick={() => openCreateTagRule()}
                        disabled={tagOptions.length === 0}
                      >
                        Добавить правило
                      </Button>
                      <Button
                        leftSection={<IconRefresh size={16} stroke={1.5} />}
                        variant="light"
                        onClick={() => void loadKnowledgeTags()}
                      >
                        Обновить
                      </Button>
                    </Group>
                  </Group>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Group justify="space-between" align="center">
                        <Stack gap={2}>
                          <Text fw={600}>Метки базы знаний</Text>
                          <Text size="sm" c="dimmed">
                            Новый справочник БЗ. Рабочие метки проектов пока не
                            изменяются.
                          </Text>
                        </Stack>
                        <Badge variant="light" color="gray">
                          {tagRows.length}
                        </Badge>
                      </Group>
                      <ScrollArea type="auto" offsetScrollbars>
                        <Table
                          striped
                          highlightOnHover
                          verticalSpacing="sm"
                          miw={960}
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Метка</Table.Th>
                              <Table.Th>Описание</Table.Th>
                              <Table.Th>Раздел</Table.Th>
                              <Table.Th>Порядок</Table.Th>
                              <Table.Th>Статус</Table.Th>
                              <Table.Th />
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {visibleTagRows.map((row) => (
                              <Table.Tr key={row.id}>
                                <Table.Td>
                                  <Group gap="xs" wrap="nowrap">
                                    <span
                                      style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: 6,
                                        background: row.color || "#B0E0E6",
                                        border:
                                          "1px solid var(--mantine-color-gray-3)",
                                        flex: "0 0 auto",
                                      }}
                                    />
                                    <Text size="sm" fw={600}>
                                      {row.name}
                                    </Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" lineClamp={3}>
                                    {row.description || "—"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  {row.project_id
                                    ? row.project_name || "Проект"
                                    : "Проект не выбран"}
                                </Table.Td>
                                <Table.Td>{row.sort_order ?? 0}</Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={row.is_active ? "green" : "gray"}
                                    variant="light"
                                  >
                                    {row.is_active ? "активна" : "выключена"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs" justify="flex-end">
                                    <Button
                                      size="compact-sm"
                                      variant="light"
                                      onClick={() => openEditTag(row)}
                                    >
                                      Изменить
                                    </Button>
                                    <Button
                                      size="compact-sm"
                                      color="red"
                                      variant="light"
                                      onClick={() => confirmDeleteTag(row)}
                                    >
                                      Удалить
                                    </Button>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                      {tagRows.length > KNOWLEDGE_TABLE_PAGE_SIZE ? (
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            Всего: {tagRows.length}
                          </Text>
                          <PaginationBar
                            page={tagsPage}
                            pageCount={tagPages}
                            onPageChange={setTagsPage}
                          />
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Group justify="space-between" align="center">
                        <Stack gap={2}>
                          <Text fw={600}>Правила меток базы знаний</Text>
                          <Text size="sm" c="dimmed">
                            Правила сохраняются в БЗ и пока не участвуют в
                            классификации обращений.
                          </Text>
                        </Stack>
                        <Badge variant="light" color="gray">
                          {tagRuleRows.length}
                        </Badge>
                      </Group>
                      <ScrollArea type="auto" offsetScrollbars>
                        <Table
                          striped
                          highlightOnHover
                          verticalSpacing="sm"
                          miw={1040}
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Правило</Table.Th>
                              <Table.Th>Метка</Table.Th>
                              <Table.Th>Ключевые слова</Table.Th>
                              <Table.Th>Раздел</Table.Th>
                              <Table.Th>Приоритет</Table.Th>
                              <Table.Th>Статус</Table.Th>
                              <Table.Th />
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {visibleTagRuleRows.map((row) => (
                              <Table.Tr key={row.id}>
                                <Table.Td>
                                  <Text size="sm" fw={600}>
                                    {row.name || "Без названия"}
                                  </Text>
                                  {row.description ? (
                                    <Text size="xs" c="dimmed" lineClamp={2}>
                                      {row.description}
                                    </Text>
                                  ) : null}
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs" wrap="nowrap">
                                    <span
                                      style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: 6,
                                        background: row.tag_color || "#B0E0E6",
                                        border:
                                          "1px solid var(--mantine-color-gray-3)",
                                        flex: "0 0 auto",
                                      }}
                                    />
                                    <Text size="sm">{row.tag_name || "—"}</Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" lineClamp={3}>
                                    {row.keywords || "—"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  {row.project_id
                                    ? row.project_name || "Проект"
                                    : "Проект не выбран"}
                                </Table.Td>
                                <Table.Td>{row.priority ?? 0}</Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={row.is_active ? "green" : "gray"}
                                    variant="light"
                                  >
                                    {row.is_active ? "активно" : "выключено"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs" justify="flex-end">
                                    <Button
                                      size="compact-sm"
                                      variant="light"
                                      onClick={() => openEditTagRule(row)}
                                    >
                                      Изменить
                                    </Button>
                                    <Button
                                      size="compact-sm"
                                      color="red"
                                      variant="light"
                                      onClick={() => confirmDeleteTagRule(row)}
                                    >
                                      Удалить
                                    </Button>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                      {tagRuleRows.length > KNOWLEDGE_TABLE_PAGE_SIZE ? (
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            Всего: {tagRuleRows.length}
                          </Text>
                          <PaginationBar
                            page={tagRulesPage}
                            pageCount={tagRulePages}
                            onPageChange={setTagRulesPage}
                          />
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
          ) : null}

          {canEdit ? (
            <Tabs.Panel
              value="readStats"
              pt="sm"
              style={{ flex: 1, minHeight: 0 }}
            >
              <ScrollArea h="100%" type="auto" offsetScrollbars>
                <Stack gap="md" pb="md">
                  <Group justify="space-between" align="center">
                    <Stack gap={2}>
                      <Text fw={600}>Статистика ознакомления</Text>
                      <Text size="sm" c="dimmed">
                        Последнее обновление:{" "}
                        {readStats?.latest_change_at
                          ? formatDateTimeDot(readStats.latest_change_at)
                          : "нет изменений"}
                      </Text>
                    </Stack>
                    <Button
                      leftSection={<IconRefresh size={16} stroke={1.5} />}
                      variant="light"
                      loading={readStatsLoading}
                      onClick={() => void loadReadStats()}
                    >
                      Обновить
                    </Button>
                  </Group>

                  <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                    <Paper withBorder radius="md" p="md">
                      <Group gap="xs">
                        <IconUsers size={18} stroke={1.5} />
                        <Text size="sm" c="dimmed">
                          Операторы
                        </Text>
                      </Group>
                      <Text fw={700} size="xl">
                        {readStats?.operators_total ?? 0}
                      </Text>
                    </Paper>
                    <Paper withBorder radius="md" p="md">
                      <Group gap="xs">
                        <IconCheck size={18} stroke={1.5} />
                        <Text size="sm" c="dimmed">
                          Ознакомились
                        </Text>
                      </Group>
                      <Text fw={700} size="xl">
                        {readStats?.operators_acknowledged ?? 0}
                      </Text>
                    </Paper>
                    <Paper withBorder radius="md" p="md">
                      <Group gap="xs">
                        <IconX size={18} stroke={1.5} />
                        <Text size="sm" c="dimmed">
                          Ожидают
                        </Text>
                      </Group>
                      <Text fw={700} size="xl">
                        {readStats?.operators_pending ?? 0}
                      </Text>
                    </Paper>
                  </SimpleGrid>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text fw={600}>По операторам</Text>
                        <Badge
                          variant={readStats?.enabled ? "light" : "outline"}
                          color={readStats?.enabled ? "green" : "gray"}
                        >
                          {readStats?.enabled
                            ? "опция включена"
                            : "опция выключена"}
                        </Badge>
                      </Group>
                      <ScrollArea.Autosize
                        mah={420}
                        type="auto"
                        offsetScrollbars
                      >
                        <Table
                          striped
                          highlightOnHover
                          verticalSpacing="sm"
                          miw={860}
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Оператор</Table.Th>
                              <Table.Th>Статус</Table.Th>
                              <Table.Th>Последний показ</Table.Th>
                              <Table.Th>Действие</Table.Th>
                              <Table.Th>Страница</Table.Th>
                              <Table.Th>Время</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {visibleReadOperatorRows.map((row) => (
                              <Table.Tr key={row.user_id}>
                                <Table.Td>
                                  <Text size="sm" fw={500}>
                                    {row.user_name}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {row.role || "—"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={
                                      row.acknowledged ? "green" : "yellow"
                                    }
                                    variant="light"
                                  >
                                    {row.acknowledged
                                      ? "ознакомился"
                                      : "ожидает"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  {row.last_shown_at
                                    ? formatDateTimeDot(row.last_shown_at)
                                    : "—"}
                                </Table.Td>
                                <Table.Td>
                                  {row.last_action
                                    ? KNOWLEDGE_VIEW_ACTION_LABELS[
                                        row.last_action
                                      ] || row.last_action
                                    : "—"}
                                </Table.Td>
                                <Table.Td>
                                  {row.last_page_seen && row.total_pages
                                    ? `${row.last_page_seen} из ${row.total_pages}`
                                    : "—"}
                                </Table.Td>
                                <Table.Td>
                                  {formatDuration(row.last_duration_seconds)}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea.Autosize>
                      {readOperatorRows.length > KNOWLEDGE_TABLE_PAGE_SIZE ? (
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            Всего: {readOperatorRows.length}
                          </Text>
                          <PaginationBar
                            page={readOperatorsPage}
                            pageCount={readOperatorPages}
                            onPageChange={setReadOperatorsPage}
                            disabled={readStatsLoading}
                          />
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Text fw={600}>История показов</Text>
                      <ScrollArea.Autosize
                        mah={420}
                        type="auto"
                        offsetScrollbars
                      >
                        <Table
                          striped
                          highlightOnHover
                          verticalSpacing="sm"
                          miw={980}
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Дата показа</Table.Th>
                              <Table.Th>Оператор</Table.Th>
                              <Table.Th>Действие</Table.Th>
                              <Table.Th>Материалов</Table.Th>
                              <Table.Th>Страница</Table.Th>
                              <Table.Th>Открыта</Table.Th>
                              <Table.Th>Закрыта</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {visibleReadHistoryRows.map((row) => (
                              <Table.Tr key={row.id}>
                                <Table.Td>
                                  {formatDateTimeDot(row.shown_at)}
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" fw={500}>
                                    {row.user_name}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {row.role || "—"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  {KNOWLEDGE_VIEW_ACTION_LABELS[row.action] ||
                                    row.action}
                                </Table.Td>
                                <Table.Td>{row.changes_count}</Table.Td>
                                <Table.Td>
                                  {row.last_page_seen} из {row.total_pages}
                                </Table.Td>
                                <Table.Td>
                                  {formatDuration(row.duration_seconds)}
                                </Table.Td>
                                <Table.Td>
                                  {row.closed_at
                                    ? formatDateTimeDot(row.closed_at)
                                    : "—"}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea.Autosize>
                      {readHistoryRows.length > KNOWLEDGE_TABLE_PAGE_SIZE ? (
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            Всего: {readHistoryRows.length}
                          </Text>
                          <PaginationBar
                            page={readHistoryPage}
                            pageCount={readHistoryPages}
                            onPageChange={setReadHistoryPage}
                            disabled={readStatsLoading}
                          />
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
          ) : null}

          {canEdit ? (
            <Tabs.Panel
              value="training"
              pt="sm"
              style={{ flex: 1, minHeight: 0 }}
            >
              <ScrollArea h="100%" type="auto" offsetScrollbars>
                <Stack gap="md" pb="md">
                  <Group justify="space-between" align="flex-end">
                    <NativeSelect
                      label="Проект"
                      value={trainingProjectId}
                      onChange={(e) =>
                        setTrainingProjectId(e.currentTarget.value)
                      }
                      data={[
                        { value: "", label: "Все проекты" },
                        ...projects.map((p) => ({
                          value: p.id,
                          label: p.name || p.slug || p.id,
                        })),
                      ]}
                    />
                    <Button
                      leftSection={<IconRefresh size={16} stroke={1.5} />}
                      variant="light"
                      loading={trainingLoading}
                      onClick={() => void loadTrainingStats()}
                    >
                      Обновить
                    </Button>
                  </Group>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Text fw={600}>
                        {editingQuestion
                          ? "Редактировать вопрос"
                          : "Новый вопрос"}
                      </Text>
                      <NativeSelect
                        label="Проект вопроса"
                        value={trainingProjectId}
                        onChange={(e) =>
                          setTrainingProjectId(e.currentTarget.value)
                        }
                        data={[
                          { value: "", label: "Выберите проект" },
                          ...projects.map((p) => ({
                            value: p.id,
                            label: p.name || p.slug || p.id,
                          })),
                        ]}
                      />
                      <Textarea
                        label="Вопрос"
                        value={questionText}
                        onChange={(e) => setQuestionText(e.currentTarget.value)}
                        minRows={2}
                      />
                      <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="sm">
                        <NativeSelect
                          label="Тип"
                          value={questionType}
                          onChange={(e) =>
                            setQuestionType(
                              e.currentTarget
                                .value as SupportKnowledgeTrainingQuestionType,
                            )
                          }
                          data={[
                            { value: "single_choice", label: "Один вариант" },
                            {
                              value: "multiple_choice",
                              label: "Несколько вариантов",
                            },
                            { value: "free_text", label: "Свободный ответ" },
                          ]}
                        />
                        <NumberInput
                          label="Баллы"
                          value={questionPoints}
                          onChange={setQuestionPoints}
                          min={1}
                          max={100}
                          step={1}
                        />
                        <NumberInput
                          label="Порядок"
                          value={questionOrder}
                          onChange={setQuestionOrder}
                          min={0}
                          step={1}
                        />
                        <Checkbox
                          label="Активен"
                          checked={questionActive}
                          onChange={(e) =>
                            setQuestionActive(e.currentTarget.checked)
                          }
                          mt={30}
                        />
                      </SimpleGrid>
                      {questionType !== "free_text" ? (
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                          <Textarea
                            label="Варианты ответа"
                            description="Каждый вариант с новой строки"
                            value={questionOptions}
                            onChange={(e) =>
                              setQuestionOptions(e.currentTarget.value)
                            }
                            minRows={4}
                          />
                          <Textarea
                            label="Правильные ответы"
                            description="Точно так же, как в вариантах; каждый с новой строки"
                            value={questionCorrect}
                            onChange={(e) =>
                              setQuestionCorrect(e.currentTarget.value)
                            }
                            minRows={4}
                          />
                        </SimpleGrid>
                      ) : null}
                      <Textarea
                        label="Комментарий / подсказка для проверки"
                        value={questionExplanation}
                        onChange={(e) =>
                          setQuestionExplanation(e.currentTarget.value)
                        }
                        minRows={2}
                      />
                      <Group justify="flex-end">
                        <Button variant="default" onClick={resetQuestionForm}>
                          Очистить
                        </Button>
                        <Button
                          loading={questionSaving}
                          onClick={() => void saveTrainingQuestion()}
                        >
                          {editingQuestion ? "Сохранить" : "Добавить вопрос"}
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Text fw={600}>Вопросы</Text>
                      <ScrollArea type="auto" offsetScrollbars>
                        <Table
                          striped
                          highlightOnHover
                          verticalSpacing="sm"
                          miw={920}
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Проект</Table.Th>
                              <Table.Th>Вопрос</Table.Th>
                              <Table.Th>Тип</Table.Th>
                              <Table.Th>Баллы</Table.Th>
                              <Table.Th>Статус</Table.Th>
                              <Table.Th />
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {visibleTrainingQuestionRows.map((row) => (
                              <Table.Tr key={row.id}>
                                <Table.Td>
                                  {projectById.get(row.project_id)?.name ||
                                    row.project_id}
                                </Table.Td>
                                <Table.Td>{row.question_text}</Table.Td>
                                <Table.Td>{row.question_type}</Table.Td>
                                <Table.Td>{row.points}</Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={row.is_active ? "green" : "gray"}
                                    variant="light"
                                  >
                                    {row.is_active ? "активен" : "выключен"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs" justify="flex-end">
                                    <Button
                                      size="compact-sm"
                                      variant="light"
                                      onClick={() => openQuestionEdit(row)}
                                    >
                                      Изменить
                                    </Button>
                                    <Button
                                      size="compact-sm"
                                      variant="light"
                                      color="red"
                                      onClick={() =>
                                        void deleteTrainingQuestion(row)
                                      }
                                    >
                                      Удалить
                                    </Button>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                      {trainingQuestionRows.length >
                      KNOWLEDGE_TABLE_PAGE_SIZE ? (
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            Всего: {trainingQuestionRows.length}
                          </Text>
                          <PaginationBar
                            page={trainingQuestionsPage}
                            pageCount={trainingQuestionPages}
                            onPageChange={setTrainingQuestionsPage}
                            disabled={trainingLoading}
                          />
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>

                  <Paper withBorder radius="md" p="md">
                    <Stack gap="sm">
                      <Text fw={600}>Ответы операторов</Text>
                      <ScrollArea type="auto" offsetScrollbars>
                        <Table
                          striped
                          highlightOnHover
                          verticalSpacing="sm"
                          miw={1050}
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Дата</Table.Th>
                              <Table.Th>Оператор</Table.Th>
                              <Table.Th>Проект</Table.Th>
                              <Table.Th>Статус</Table.Th>
                              <Table.Th>Результат</Table.Th>
                              <Table.Th>Проверка</Table.Th>
                              <Table.Th />
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {visibleTrainingAttemptRows.map((row) => (
                              <Table.Tr key={row.id}>
                                <Table.Td>
                                  {formatDateTimeDot(
                                    row.submitted_at || row.started_at,
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  {row.user_name || row.user_id}
                                </Table.Td>
                                <Table.Td>
                                  {row.project_name || row.project_id}
                                </Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={
                                      row.status === "passed"
                                        ? "green"
                                        : row.status === "failed"
                                          ? "red"
                                          : "yellow"
                                    }
                                    variant="light"
                                  >
                                    {row.status === "passed"
                                      ? "пройдено"
                                      : row.status === "failed"
                                        ? "не пройдено"
                                        : "на проверке"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  {row.score} / {row.max_score} · порог{" "}
                                  {row.threshold_percent}%
                                </Table.Td>
                                <Table.Td>
                                  {row.auto_checked
                                    ? "авто"
                                    : row.reviewed_by_name || "ручная"}
                                </Table.Td>
                                <Table.Td>
                                  <Button
                                    size="compact-sm"
                                    variant="light"
                                    onClick={() => {
                                      setReviewTarget(row);
                                      setReviewStatus(
                                        row.status === "failed"
                                          ? "failed"
                                          : "passed",
                                      );
                                      setReviewScore(row.score);
                                      setReviewComment(
                                        row.reviewer_comment || "",
                                      );
                                    }}
                                  >
                                    Проверить
                                  </Button>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                      {trainingAttemptRows.length >
                      KNOWLEDGE_TABLE_PAGE_SIZE ? (
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            Всего: {trainingAttemptRows.length}
                          </Text>
                          <PaginationBar
                            page={trainingAttemptsPage}
                            pageCount={trainingAttemptPages}
                            onPageChange={setTrainingAttemptsPage}
                            disabled={trainingLoading}
                          />
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
          ) : null}
        </Tabs>
      </Stack>

      <Drawer
        opened={!isWide && !!viewArticle}
        onClose={() => void selectArticle(null)}
        title={viewArticle?.title || "Материал"}
        position="right"
        size="lg"
        padding="md"
      >
        {articleHistory.current.length > 0 && <Button variant="subtle" onClick={() => { const previous = articleHistory.current.pop(); if (previous) void selectArticle(previous, true); }}>Назад к предыдущему материалу</Button>}
        {viewArticle ? (
          <DetailPanel
            article={viewArticle}
            loading={viewArticleLoading}
            projectById={projectById}
            onExport={() =>
              exportSingleArticle(
                viewArticle.project_id
                  ? (projectById.get(viewArticle.project_id) ?? null)
                  : null,
                viewArticle,
              )
            }
            onImport={(file) =>
              importSingleArticle(
                viewArticle.project_id
                  ? (projectById.get(viewArticle.project_id) ?? null)
                  : null,
                viewArticle,
                file,
              )
            }
            canEdit={canEdit}
            variableDefinitions={getVariableDefinitionsForArticle(viewArticle)}
            relatedArticles={allArticles}
            onArticleClick={(article) => void selectArticle(article)}
            onEdit={() => {
              const project = viewArticle.project_id
                ? (projectById.get(viewArticle.project_id) ?? null)
                : null;
              void openEdit(project, viewArticle);
              void selectArticle(null);
            }}
          />
        ) : null}
      </Drawer>

      <AppModal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        title={editArticle ? "Редактировать материал" : "Новый материал"}
        size="min(1120px, calc(100vw - 32px))"
        keepMounted={false}
      >
        <Stack gap="md">
          {!editArticle ? (
            <NativeSelect
              label="Раздел"
              description="Общие материалы доступны во всех проектах, проектные — только в выбранном проекте"
              value={formScopeId}
              onChange={(e) => setFormScopeId(e.currentTarget.value)}
              data={[
                { value: KNOWLEDGE_GENERAL_TAB_ID, label: "Общие" },
                ...projects.map((p) => ({
                  value: p.id,
                  label: p.name || p.slug || p.id,
                })),
              ]}
              required
            />
          ) : null}

          {similarItems.length > 0 ? (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Похожие материалы уже есть
              </Text>
              {similarItems.map((item) => (
                <Text key={item.id} size="sm">
                  {item.title}{" "}
                  <Text span size="xs" c="dimmed">
                    ({item.material_type_label},{" "}
                    {Math.round(item.relevance_score * 100)}%)
                  </Text>
                </Text>
              ))}
              {similarLoading ? (
                <Text size="xs" c="dimmed">
                  Проверяем совпадения…
                </Text>
              ) : null}
            </Stack>
          ) : null}

          <TextInput
            label="Заголовок"
            placeholder="Например: Оплата картой — инструкция"
            value={formTitle}
            onChange={(e) => setFormTitle(e.currentTarget.value)}
            required
          />
          <NativeSelect
            label="Тип материала"
            value={formType}
            onChange={(e) =>
              setFormType(e.currentTarget.value as SupportKnowledgeMaterialType)
            }
            data={effectiveMaterialTypes.map((t) => ({
              value: t.key,
              label: t.label,
            }))}
          />
          <TextInput
            label="Теги"
            description="Через запятую — для поиска и группировки"
            placeholder="оплата, billing, возврат"
            value={formTags}
            onChange={(e) => setFormTags(e.currentTarget.value)}
          />
          <Text size="sm" fw={500} mb={4}>
            Содержимое
          </Text>
          {formOpen ? (
            <KnowledgeRichTextEditor
              key={formEditorKey}
              value={formContent}
              onChange={setFormContent}
              placeholder="Текст инструкции, шаблон ответа или скрипт для оператора. Можно вставлять изображения."
              minHeight={260}
              articleTitles={articleTitleOptions}
            />
          ) : null}
          {canEdit ? (
            <KnowledgeArticleAttachments
              articleId={editArticle?.id}
              attachments={formAttachments}
              pendingFiles={pendingFiles}
              canEdit
              onAttachmentsChange={setFormAttachments}
              onPendingChange={setPendingFiles}
            />
          ) : null}
          <Checkbox
            label="Активен (виден операторам)"
            checked={formActive}
            onChange={(e) => setFormActive(e.currentTarget.checked)}
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setFormOpen(false)}>
              Отмена
            </Button>
            <Button loading={saving} onClick={() => void submitForm()}>
              {editArticle ? "Сохранить" : "Добавить"}
            </Button>
          </Group>
        </Stack>
      </AppModal>

      <AppModal
        opened={variableModalOpen}
        onClose={() => setVariableModalOpen(false)}
        title={
          editingVariable ? "Редактировать переменную" : "Новая переменная"
        }
        size="lg"
        keepMounted={false}
      >
        <Stack gap="md">
          <NativeSelect
            label="Раздел"
            description="Общие переменные применяются ко всем проектам"
            value={variableProjectId}
            onChange={(e) => setVariableProjectId(e.currentTarget.value)}
            data={[
              { value: KNOWLEDGE_GENERAL_TAB_ID, label: "Общие" },
              ...projects.map((p) => ({
                value: p.id,
                label: p.name || p.slug || p.id,
              })),
            ]}
          />
          <TextInput
            label="Ключ"
            description="Используется в материалах как [ключ]"
            placeholder="email"
            value={variableKey}
            onChange={(e) => setVariableKey(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Определение"
            placeholder="Email пользователя"
            value={variableDefinition}
            onChange={(e) => setVariableDefinition(e.currentTarget.value)}
            minRows={4}
            required
          />
          <NumberInput
            label="Порядок"
            value={variableOrder}
            onChange={setVariableOrder}
            min={0}
            step={1}
          />
          <Checkbox
            label="Активна"
            checked={variableActive}
            onChange={(e) => setVariableActive(e.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setVariableModalOpen(false)}
            >
              Отмена
            </Button>
            <Button
              loading={variableSaving}
              onClick={() => void saveVariable()}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </AppModal>

      <AppModal
        opened={tagModalOpen}
        onClose={() => setTagModalOpen(false)}
        title={editingTag ? "Редактировать метку" : "Новая метка"}
        size="lg"
        keepMounted={false}
      >
        <Stack gap="md">
          <NativeSelect
            label="Раздел"
            value={tagProjectId}
            onChange={(e) => setTagProjectId(e.currentTarget.value)}
            data={[
              { value: KNOWLEDGE_GENERAL_TAB_ID, label: "Общие" },
              ...projects.map((p) => ({
                value: p.id,
                label: p.name || p.slug || p.id,
              })),
            ]}
          />
          <TextInput
            label="Название"
            placeholder="Оплата"
            value={tagName}
            onChange={(e) => setTagName(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Описание"
            placeholder="Когда использовать эту метку"
            value={tagDescription}
            onChange={(e) => setTagDescription(e.currentTarget.value)}
            minRows={3}
          />
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput
              label="Цвет"
              placeholder="#B0E0E6"
              value={tagColor}
              onChange={(e) => setTagColor(e.currentTarget.value)}
            />
            <NumberInput
              label="Порядок"
              value={tagOrder}
              onChange={setTagOrder}
              min={0}
              step={1}
            />
            <Checkbox
              label="Активна"
              checked={tagActive}
              onChange={(e) => setTagActive(e.currentTarget.checked)}
              mt={30}
            />
          </SimpleGrid>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setTagModalOpen(false)}>
              Отмена
            </Button>
            <Button loading={tagSaving} onClick={() => void saveTag()}>
              Сохранить
            </Button>
          </Group>
        </Stack>
      </AppModal>

      <AppModal
        opened={tagRuleModalOpen}
        onClose={() => setTagRuleModalOpen(false)}
        title={editingTagRule ? "Редактировать правило" : "Новое правило"}
        size="lg"
        keepMounted={false}
      >
        <Stack gap="md">
          <NativeSelect
            label="Метка"
            value={tagRuleTagId}
            onChange={(e) => setTagRuleTagId(e.currentTarget.value)}
            data={
              tagOptions.length
                ? tagOptions
                : [{ value: "", label: "Сначала добавьте метку" }]
            }
          />
          <TextInput
            label="Название правила"
            placeholder="Определение обращения по оплате"
            value={tagRuleName}
            onChange={(e) => setTagRuleName(e.currentTarget.value)}
          />
          <Textarea
            label="Ключевые слова"
            description="Можно указывать через запятую или с новой строки"
            placeholder="оплата, платёж, чек, списание"
            value={tagRuleKeywords}
            onChange={(e) => setTagRuleKeywords(e.currentTarget.value)}
            minRows={4}
          />
          <Textarea
            label="Описание"
            placeholder="Условия, исключения или пояснение к правилу"
            value={tagRuleDescription}
            onChange={(e) => setTagRuleDescription(e.currentTarget.value)}
            minRows={3}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <NumberInput
              label="Приоритет"
              value={tagRulePriority}
              onChange={setTagRulePriority}
              step={1}
            />
            <Checkbox
              label="Активно"
              checked={tagRuleActive}
              onChange={(e) => setTagRuleActive(e.currentTarget.checked)}
              mt={30}
            />
          </SimpleGrid>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setTagRuleModalOpen(false)}
            >
              Отмена
            </Button>
            <Button
              loading={tagRuleSaving}
              onClick={() => void saveTagRule()}
              disabled={!tagRuleTagId}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </AppModal>

      <AppModal
        opened={guideOpen}
        onClose={() => setGuideOpen(false)}
        title="Гайд по базе знаний"
        size="xl"
      >
        <ScrollArea h="min(72vh, 760px)" type="auto" offsetScrollbars>
          <Paper
            withBorder
            radius="lg"
            p={{ base: "md", sm: "lg" }}
            style={{
              background: "light-dark(#ffffff, var(--mantine-color-dark-7))",
            }}
          >
            <Stack gap="xs" mb="md">
              <Text fw={700} size="lg">
                Как заполнять базу знаний
              </Text>
              <Text size="sm" c="dimmed">
                Практическая подсказка для редактора: что писать в статье, какие
                блоки вставляются в обращение и как связывать материалы между
                собой.
              </Text>
            </Stack>
            <KnowledgeArticleContent
              content={KNOWLEDGE_BASE_GUIDE_HTML}
              contentFormat="html"
            />
          </Paper>
        </ScrollArea>
      </AppModal>

      <KnowledgeBaseExcelPanel
        opened={excelOpen}
        onClose={() => setExcelOpen(false)}
        projects={projects}
        articles={allArticles}
        materialTypes={effectiveMaterialTypes}
        selectedProjectId={selectedProjectId}
        canImport={canKbExcel}
        onImported={loadAll}
      />

      <AppModal
        opened={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        title="Проверка ответов"
        size="lg"
        footer={
          <>
            <Button variant="default" onClick={() => setReviewTarget(null)}>
              Отмена
            </Button>
            <Button onClick={() => void reviewAttempt()}>
              Сохранить проверку
            </Button>
          </>
        }
      >
        <Stack gap="md">
          {reviewTarget ? (
            <>
              <Text size="sm" c="dimmed">
                {reviewTarget.user_name || reviewTarget.user_id} ·{" "}
                {reviewTarget.project_name || reviewTarget.project_id}
              </Text>
              <ScrollArea.Autosize mah={360} type="auto" offsetScrollbars>
                <Stack gap="sm" pr={4}>
                  {reviewTarget.answers.map((answer, idx) => (
                    <Paper key={idx} withBorder radius="md" p="sm">
                      <Stack gap={4}>
                        <Text fw={600} size="sm">
                          {String(answer.question_text || "")}
                        </Text>
                        <Text size="sm">
                          Ответ:{" "}
                          {Array.isArray(answer.answer)
                            ? answer.answer.join(", ")
                            : String(answer.answer || "—")}
                        </Text>
                        {answer.is_correct != null ? (
                          <Badge
                            color={answer.is_correct ? "green" : "red"}
                            variant="light"
                            w="fit-content"
                          >
                            {answer.is_correct ? "верно" : "ошибка"}
                          </Badge>
                        ) : null}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
              <NativeSelect
                label="Решение"
                value={reviewStatus}
                onChange={(e) =>
                  setReviewStatus(e.currentTarget.value as "passed" | "failed")
                }
                data={[
                  { value: "passed", label: "Пройдено" },
                  { value: "failed", label: "Не пройдено" },
                ]}
              />
              <NumberInput
                label="Баллы"
                value={reviewScore}
                onChange={setReviewScore}
                min={0}
                max={reviewTarget.max_score}
                step={1}
              />
              <Textarea
                label="Комментарий"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.currentTarget.value)}
                minRows={2}
              />
            </>
          ) : null}
        </Stack>
      </AppModal>
    </PageShell>
  );
}
